// components/models/TopazImageEnhanceControlPanel.tsx
import React, { useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import RangeControl from '../ui/RangeControl';
import Switch from '../ui/Switch';
import { TopazImageEnhanceModel, TopazImageEnhanceParams, TopazImageInputMegaPixels, TopazImageOutputMegaPixels } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const defaultTopazImageEnhanceParams: TopazImageEnhanceParams = {
  prompt: '',
  topazModel: 'Standard V2',
  model: 'Standard V2',
  inputMegaPixels: 5,
  outputMegaPixels: 40,
  crop_to_fill: false,
  output_format: 'jpeg',
  subject_detection: 'All',
  face_enhancement: true,
  face_enhancement_strength: 0.8,
  face_enhancement_creativity: 0,
  topazOutputFormat: 'jpeg',
  autoprompt: false,
  seed: -1,
};

export const getDefaultTopazImageEnhanceParams = (): TopazImageEnhanceParams => ({
  ...defaultTopazImageEnhanceParams,
});

const topazImageModelOptions: TopazImageEnhanceModel[] = [
  'Low Resolution V2',
  'Standard V2',
  'CGI',
  'High Fidelity V2',
  'Text Refine',
  'Recovery',
  'Redefine',
  'Recovery V2',
  'Standard MAX',
  'Wonder',
  'Wonder 3',
];

const SUBJECT_DETECTION_OPTIONS = ['All', 'Foreground', 'Background'];
const ENHANCEMENT_STRENGTH_OPTIONS = ['auto', 'low', 'medium', 'high'];
const TOPAZ_INPUT_RESOLUTION_OPTIONS = [
  { value: 'auto', label: 'Auto (16 Mpx)' },
  { value: '0.5', label: '0.5 Mpx' },
  { value: '1', label: '1 Mpx' },
  { value: '2', label: '2 Mpx' },
  { value: '5', label: '5 Mpx' },
  { value: '10', label: '10 Mpx' },
  { value: '16', label: '16 Mpx' },
];
const TOPAZ_OUTPUT_RESOLUTION_OPTIONS = [
  { value: '16', label: '16 Mpx' },
  { value: '30', label: '30 Mpx' },
  { value: '40', label: '40 Mpx' },
];

const SHARPEN_DENOISE_MODELS = new Set<TopazImageEnhanceModel>([
  'Standard V2',
  'Low Resolution V2',
  'CGI',
  'High Fidelity V2',
  'Text Refine',
  'Redefine',
]);
const FIX_COMPRESSION_MODELS = new Set<TopazImageEnhanceModel>([
  'Standard V2',
  'Low Resolution V2',
  'High Fidelity V2',
  'Text Refine',
]);

const TopazImageEnhanceControlPanel: React.FC = React.memo(() => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as TopazImageEnhanceParams | undefined
  );

  const updateParams = useCallback((
    name: keyof TopazImageEnhanceParams,
    value: TopazImageEnhanceParams[keyof TopazImageEnhanceParams]
  ) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[TopazImageEnhanceControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });

    if (name === 'prompt' && typeof value === 'string') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: value.length,
        model_name: diffusionModel,
        source_component: 'TopazImageEnhanceControlPanel',
      });
      return;
    }

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'TopazImageEnhanceControlPanel',
    });
  }, [kernel]);

  const handleModelChange = useCallback((value: string) => {
    const model = value as TopazImageEnhanceModel;
    updateParams('topazModel', model);
    updateParams('model', model);
  }, [updateParams]);

  const handleInputResolutionChange = useCallback((value: string) => {
    if (value === 'auto' || value === '0.5' || value === '1' || value === '2' || value === '5' || value === '10' || value === '16') {
      updateParams('inputMegaPixels', value === 'auto' ? 'auto' : Number(value) as TopazImageInputMegaPixels);
    }
  }, [updateParams]);

  const handleOutputResolutionChange = useCallback((value: string) => {
    const parsed = Number(value);
    if (parsed === 16 || parsed === 30 || parsed === 40) {
      updateParams('outputMegaPixels', parsed as TopazImageOutputMegaPixels);
    }
  }, [updateParams]);

  const handleNumericParamChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateParams(name as keyof TopazImageEnhanceParams, Number(value));
  }, [updateParams]);

  const handleCropToFillChange = useCallback((checked: boolean) => {
    updateParams('crop_to_fill', checked);
  }, [updateParams]);

  const handleSubjectDetectionChange = useCallback((value: string) => {
    updateParams('subject_detection', value as TopazImageEnhanceParams['subject_detection']);
  }, [updateParams]);

  const handleFaceEnhancementChange = useCallback((checked: boolean) => {
    updateParams('face_enhancement', checked);
  }, [updateParams]);

  const handlePromptChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateParams('prompt', event.target.value);
  }, [updateParams]);

  const handleAutopromptChange = useCallback((checked: boolean) => {
    updateParams('autoprompt', checked);
  }, [updateParams]);

  const handleEnhancementStrengthChange = useCallback((value: string) => {
    updateParams(
      'enhancement_strength',
      value === 'auto' ? undefined : (value as TopazImageEnhanceParams['enhancement_strength'])
    );
  }, [updateParams]);

  if (!params) return null;

  const selectedModel = (params.topazModel ?? params.model ?? 'Standard V2') as TopazImageEnhanceModel;
  const inputMegaPixels = params.inputMegaPixels ?? 5;
  const outputMegaPixels = params.outputMegaPixels ?? 40;
  const cropToFill = params.crop_to_fill ?? false;
  const subjectDetection = params.subject_detection ?? 'All';
  const faceEnhancement = params.face_enhancement ?? true;
  const faceEnhancementCreativity = typeof params.face_enhancement_creativity === 'number' ? params.face_enhancement_creativity : 0;
  const faceEnhancementStrength = typeof params.face_enhancement_strength === 'number' ? params.face_enhancement_strength : 0.8;
  const sharpen = typeof params.sharpen === 'number' ? params.sharpen : 0;
  const denoise = typeof params.denoise === 'number' ? params.denoise : 0;
  const fixCompression = typeof params.fix_compression === 'number' ? params.fix_compression : 0;
  const textRefineStrength = typeof params.strength === 'number' ? params.strength : 0.5;
  const redefineCreativity = typeof params.creativity === 'number' ? params.creativity : 3;
  const redefineTexture = typeof params.texture === 'number' ? params.texture : 3;
  const redefineAutoprompt = params.autoprompt ?? false;
  const recoveryV2Detail = typeof params.detail === 'number' ? params.detail : 0.5;
  const wonder3EnhancementStrength = params.enhancement_strength ?? 'auto';

  const supportsSubjectAndFace = selectedModel === 'Standard V2' || selectedModel === 'Recovery V2';
  const supportsSharpenDenoise = SHARPEN_DENOISE_MODELS.has(selectedModel);
  const supportsFixCompression = FIX_COMPRESSION_MODELS.has(selectedModel);
  const isTextRefine = selectedModel === 'Text Refine';
  const isRedefine = selectedModel === 'Redefine';
  const isRecoveryV2 = selectedModel === 'Recovery V2';
  const isWonder3 = selectedModel === 'Wonder 3';

  return (
    <div className="space-y-4">
      <Select
        id="topaz-image-model-select"
        label="Enhancement Model"
        value={selectedModel}
        options={topazImageModelOptions}
        onChange={handleModelChange}
      />
      <Select
        id="topaz-image-input-megapixels"
        label="Input Resolution"
        value={String(inputMegaPixels)}
        options={TOPAZ_INPUT_RESOLUTION_OPTIONS}
        onChange={handleInputResolutionChange}
      />
      <Select
        id="topaz-image-output-megapixels"
        label="Output Resolution"
        value={String(outputMegaPixels)}
        options={TOPAZ_OUTPUT_RESOLUTION_OPTIONS}
        onChange={handleOutputResolutionChange}
      />

      <div className="flex items-center space-x-2">
        <Switch
          checked={cropToFill}
          onCheckedChange={handleCropToFillChange}
          labelRight="Crop To Fill"
        />
      </div>

      {supportsSubjectAndFace && (
        <>
          <Select
            id="topaz-image-subject-detection-select"
            label="Subject Detection"
            value={subjectDetection}
            options={SUBJECT_DETECTION_OPTIONS}
            onChange={handleSubjectDetectionChange}
          />
          <div className="flex items-center space-x-2">
            <Switch
              checked={faceEnhancement}
              onCheckedChange={handleFaceEnhancementChange}
              labelRight="Face Enhancement"
            />
          </div>
          {faceEnhancement && (
            <>
              <RangeControl
                label="Face Enhancement Creativity"
                name="face_enhancement_creativity"
                min={0}
                max={1}
                step={0.01}
                value={faceEnhancementCreativity}
                onChange={handleNumericParamChange}
              />
              <RangeControl
                label="Face Enhancement Strength"
                name="face_enhancement_strength"
                min={0}
                max={1}
                step={0.01}
                value={faceEnhancementStrength}
                onChange={handleNumericParamChange}
              />
            </>
          )}
        </>
      )}

      {supportsSharpenDenoise && (
        <>
          <RangeControl
            label="Sharpen"
            name="sharpen"
            min={0}
            max={1}
            step={0.01}
            value={sharpen}
            onChange={handleNumericParamChange}
          />
          <RangeControl
            label="Denoise"
            name="denoise"
            min={0}
            max={1}
            step={0.01}
            value={denoise}
            onChange={handleNumericParamChange}
          />
        </>
      )}

      {supportsFixCompression && (
        <RangeControl
          label="Fix Compression"
          name="fix_compression"
          min={0}
          max={1}
          step={0.01}
          value={fixCompression}
          onChange={handleNumericParamChange}
        />
      )}

      {isTextRefine && (
        <RangeControl
          label="Strength"
          name="strength"
          min={0.01}
          max={1}
          step={0.01}
          value={textRefineStrength}
          onChange={handleNumericParamChange}
          description="Enhancement strength for Text Refine."
        />
      )}

      {isRedefine && (
        <>
          <RangeControl
            label="Creativity"
            name="creativity"
            min={1}
            max={6}
            step={1}
            value={redefineCreativity}
            onChange={handleNumericParamChange}
          />
          <RangeControl
            label="Texture"
            name="texture"
            min={1}
            max={5}
            step={1}
            value={redefineTexture}
            onChange={handleNumericParamChange}
          />
          <AutoGrowingTextarea
            label="Redefine Prompt"
            placeholder="Guide generative upscaling..."
            name="prompt"
            value={params.prompt ?? ''}
            onChange={handlePromptChange}
            maxHeight={240}
          />
          <div className="flex items-center space-x-2">
            <Switch
              checked={redefineAutoprompt}
              onCheckedChange={handleAutopromptChange}
              labelRight="Autoprompt"
            />
          </div>
        </>
      )}

      {isRecoveryV2 && (
        <RangeControl
          label="Detail"
          name="detail"
          min={0}
          max={1}
          step={0.01}
          value={recoveryV2Detail}
          onChange={handleNumericParamChange}
        />
      )}

      {isWonder3 && (
        <Select
          id="topaz-image-enhancement-strength-select"
          label="Enhancement Strength"
          value={wonder3EnhancementStrength}
          options={ENHANCEMENT_STRENGTH_OPTIONS}
          onChange={handleEnhancementStrengthChange}
        />
      )}

      <p className="text-xs text-[#777777]">
        Upscales and enhances the current generation area via fal Topaz. The effective provider factor is derived from input/output resolution and limited to 1–4. Prompt/reference images are ignored except where model-specific options apply.
      </p>
    </div>
  );
});

TopazImageEnhanceControlPanel.displayName = 'TopazImageEnhanceControlPanel';

export default TopazImageEnhanceControlPanel;
